import * as React from "react";
import { Snackbar, Alert, type AlertColor } from "@mui/material";

interface Notification {
  id: number;
  message: string;
  severity: AlertColor;
}

interface NotificationContextValue {
  notify: (message: string, severity?: AlertColor) => void;
}

const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [queue, setQueue] = React.useState<Notification[]>([]);
  const nextId = React.useRef(0);

  const notify = React.useCallback((message: string, severity: AlertColor = "info") => {
    nextId.current += 1;
    setQueue((prev) => [...prev, { id: nextId.current, message, severity }]);
  }, []);

  const handleClose = React.useCallback((id: number) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const value = React.useMemo(() => ({ notify }), [notify]);
  const current = queue[0];

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={Boolean(current)}
        autoHideDuration={5000}
        onClose={() => current && handleClose(current.id)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {current ? (
          <Alert
            onClose={() => handleClose(current.id)}
            severity={current.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const ctx = React.useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
}
